import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AlertComponent } from './alert.component';
import { AlertButton } from '@shared/alert/alertButton';
import { BsModalService } from 'ngx-bootstrap/modal';
import { TranslationParams } from '../manager/locale-resource.manager';

export type MessageType = "info" | "help" | "exclamation" | "error" | "class-ok" | "";

interface MessageArgs {
  /** translation params used for the message */
  translationParams?: TranslationParams,
  type?: MessageType,
  escapeTranslationParams?: boolean,
}

@Injectable({ providedIn: 'root' })
export class AlertHelper {
  constructor(
    private bsModalService: BsModalService,
  ) {}

  public async showError(title: string, message: string = '', args?: MessageArgs): Promise<AlertButton> {
    return this.showMessage(title, message, args);
  }

  /** shows an alert that has no functionality except waiting for the user to click ok */
  public async showMessage(title: string, message: string, args?: MessageArgs): Promise<AlertButton> {
    return this.showAlert(
      title,
      message,
      args?.translationParams ?? {},
      [ AlertButton.OK ],
      args?.type ?? 'info',
      null,
      true,
      false,
      args?.escapeTranslationParams ?? true);
  }

  public async show(title: string, message: string, options: {
    translationParams?: TranslationParams,
    buttonFlags?: AlertButton[],
    iconClass?: MessageType,
    buttonLabels?: ButtonLabels | null,
    animated?: boolean,
    ignoreBackdropClick?: boolean,
    escapeTranslationParams?: boolean
  } = {}) {
    return this.showAlert(
      title,
      message,
      options.translationParams ?? {},
      options.buttonFlags ?? [AlertButton.OK],
      options.iconClass ?? '',
      options.buttonLabels ?? null,
      options.animated ?? true,
      options.ignoreBackdropClick ?? false,
      options.escapeTranslationParams ?? true,
    );
  }

  public async showAlert(
    title: string,
    message: string,
    translationParams: TranslationParams = {},
    buttonFlags: AlertButton[],
    iconClass: MessageType = '',
    buttonLabels: ButtonLabels | null = null,
    animated: boolean = true,
    ignoreBackdropClick: boolean = false,
    escapeTranslationParams: boolean = true
  ): Promise<AlertButton>  {
    const previouslyActiveElement = document.activeElement;

    if(Array.isArray(translationParams)) {
      const obj: TranslationParams = {};
      for (let i = 0; i < translationParams.length; i++) {
        obj[i.toString()] = translationParams[i];
      }

      translationParams = obj;
    }

    /* We don't want any active inputElement etc. to remain focussed behind the modal,
    so we unfocus it and refocus it again after closing the modal */
    if (previouslyActiveElement instanceof HTMLElement) previouslyActiveElement.blur();

    const config: Partial<AlertComponent> = {
      title,
      message,
      translationParams,
      buttonFlags,
      iconClass,
      escapeTranslationParams,
    };

    if (buttonLabels) {
      config.yesButtonLabel = buttonLabels.yesLabel;
      config.noButtonLabel = buttonLabels.noLabel;
      config.nextButtonLabel = buttonLabels.nextLabel;
    }

    const bsModal = this.bsModalService.show(AlertComponent, {
      animated,
      ignoreBackdropClick: ignoreBackdropClick,
      initialState: config,
    });

    const alertResult = await firstValueFrom(bsModal.content!.finish);
    if (previouslyActiveElement instanceof HTMLElement) previouslyActiveElement.focus();
    return alertResult;
  }
}

export type ButtonLabels = {
  yesLabel?: string,
  noLabel?: string,
  nextLabel?: string
}
